import React, { useState } from 'react';
import classNames from 'classnames';
import { AlertTriangle, AlertCircle, Check } from 'react-feather';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import { hasFeatureFlags } from 'utils/features';
import trackEvent from 'misc/trackEvent';

import { Text, TEXT_VARIANTS } from 'ui/Typography';

import { Icon } from 'ui';

import HighFiveShareableLink from 'components/shared/HighFiveShareableLink';
import SkippedSuccess from 'components/Practice/Recording/SkippedSuccess';
import SkippedFailure from 'components/Practice/Recording/SkippedFailure';
import SkippedOption from 'components/Practice/Recording/SkippedOption';
import { useFeatureFlags } from 'hooks/index';
import WebcamRecordingRepeatableQuestion from '../WebcamRecordingRepeatableQuestion';
import ScreenRecordingRepeatableQuestion from '../ScreenRecordingRepeatableQuestion';
import AudioRecordingRepeatableQuestion from '../AudioRecordingRepeatableQuestion';
import UploadFileTypeError from './UploadFileTypeError';

const TAB_RESPONSES = 'responses';

const RepeatableRecordingQuestion = props => {
  const {
    i18n,
    data,
    meta,
    resetAnswer,
    setFinishedRecording,
    setIsTabDisabled,
    setCurrentChoice,
    setSelectedTab,
    setSkipped,
    skipped,
    setTechnicalDifficultiesReported,
    technicalDifficultiesReported,
    userResponses,
    givenAnswer,
    setGivenAnswer,
    sendFeedbackError,
    setSendFeedbackError,
    enabledAutomatedAnalysis,
    setEnabledAutomatedAnalysis,
    finished,
  } = props;
  const { questionId, progressId, isGradable, questionType } = data;
  const { features, previewing, completedAt } = meta;

  const [videoUploading, setVideoUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadTypeError, setUploadTypeError] = useState(false);
  const disabled = previewing;
  const showSkipOption =
    !finished && !skipped && !features.removeAbilityToSkipRecordingQuestions;
  const showReportTechnicalDifficulties =
    features.removeAbilityToSkipRecordingQuestions;
  const skipDisabled = previewing || finished;

  const hasZiggeoForAllMedia = hasFeatureFlags(['ziggeo_for_all_media']);
  const seismicRecordingEnabled = useFeatureFlags(
    'readiness-enable-seismic-recording',
  );

  const seismicBiometricCompliance = useFeatureFlags(
    'readiness-enable-biometric-compliance',
  );

  let provider = '';
  if (questionType === 'webcam_recording') {
    provider = 'ziggeo';
  } else if (questionType === 'screen_recording' && hasZiggeoForAllMedia) {
    provider = 'ziggeo';
  } else {
    provider = 'hippo_video';
  }

  /************* TEXT and STYLES ******************/
  let recordButtonText = '';
  if (seismicRecordingEnabled) {
    if (questionType === 'screen_recording') {
      recordButtonText = t('questions.question.hippo_screen_recording.record');
    } else if (questionType === 'webcam_recording') {
      recordButtonText = t('questions.question.webcam_recording.record');
    } else {
      recordButtonText = t('questions.question.audio_recording.record');
    }
  } else if (provider === 'ziggeo') {
    recordButtonText = t('lessons.editor.media_recording.record');
  } else if (questionType === 'screen_recording') {
    recordButtonText = t('questions.question.hippo_screen_recording.record');
  } else {
    recordButtonText = t('questions.question.audio_recording.record');
  }

  let uploadButtonText = '';
  if (provider === 'ziggeo') {
    uploadButtonText = t('questions.question.webcam_recording.upload');
  } else if (questionType === 'screen_recording') {
    uploadButtonText = t('questions.question.hippo_screen_recording.upload');
  } else {
    uploadButtonText = t('questions.question.audio_recording.upload');
  }

  const recorderStyles = classNames('VideoEmbed RecordingQuestion-player');
  const previewingStyles = classNames({
    'VideoEmbed--disabled': previewing || userResponses.length === 10,
  });
  const mediaButtonClass = classNames({
    'QuestionElement--disabled': disabled,
  });
  const warningStyles = classNames({
    'QuestionElement--limitWarning': userResponses.length >= 9,
  });
  const containerClasses = classNames('PracticeQuestion VideoResponseQuestion');

  /**************** WEBCAM FUNCTIONS *******************/

  const saveMedia = mediaData => {
    const mediaRequest = {
      question_id: questionId,
      answer: mediaData.token,
      provider: mediaData.isSeismic ? 'seismic' : provider,
      file_format: mediaData?.file_format || mediaData?.format,
    };
    request
      .post(routes.LESSON_ATTEMPTS(props.data.lessonId), mediaRequest)
      .then(response => {
        setGivenAnswer(null);
        setFinishedRecording(true);
        setIsTabDisabled(false);
        setSelectedTab(TAB_RESPONSES);
        setCurrentChoice(response.attempt);
      })
      .catch(e => {
        console.error(e);
        setErrorMessage(t('common.server_error'));
      });
  };

  const setUploadFinished = () => {
    setVideoUploading(false);
    setIsTabDisabled(false);
  };

  const setUploadStarted = () => {
    setVideoUploading(true);
    setIsTabDisabled(true);
  };

  const handleBadUploadType = () => {
    setUploadTypeError(true);
    setErrorMessage(t('questions.question.video_response.invalid_file_type'));
  };

  const reloadRecording = () => {
    setUploadTypeError(false);
    setErrorMessage('');
    setVideoUploading(false);
  };

  /*************** SKIPPING FUNCTIONS *******************/
  const canSkipWithReasonAndFeedback = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      props.meta.skippedUrl &&
      !features.removeAbilityToSkipRecordingQuestions
    );
  };

  const canReportTechnicalDifficulties = (feedback, reason) => {
    return (
      feedback &&
      reason &&
      props.meta.skippedUrl &&
      features.removeAbilityToSkipRecordingQuestions
    );
  };

  const trackMediaAnswerSkipped = (feedback, reason, answer = null) => {
    trackEvent('practice/media_answer_skipped', {
      answer_id: answer?.id,
      question_id: props.data.questionId,
      question_type: props.data.questionType,
      skip_reason: reason,
      skip_feedback: feedback,
    });
  };

  const sendTechnicalIssueMailer = (reason, feedback, skippedState) => {
    request
      .post(
        routes.PRACTICE.TECHNICAL_ISSUES_MAILER.CREATE(props.data.questionId),
        {
          assignor_id: props.data.assignorId,
          skip_reason: reason,
          skip_feedback: feedback,
        },
      )
      .then(responseData => {
        if (!responseData.success) {
          setSendFeedbackError(true);
        }
        setSkipped(skippedState);
        setTechnicalDifficultiesReported(!skippedState);
      });
  };

  const skipRecording = (feedback = null, reason = null) => {
    if (canSkipWithReasonAndFeedback(feedback, reason)) {
      request
        .post(meta.skippedUrl, {
          question_id: data.questionId,
          skipped: true,
          feedback,
          reason,
        })
        .then(responseData => {
          const answer = responseData.answer?.answer;
          trackMediaAnswerSkipped(feedback, reason, answer);
        });
      sendTechnicalIssueMailer(reason, feedback, true);
      setCurrentChoice(null);
    } else if (canReportTechnicalDifficulties(feedback, reason)) {
      trackMediaAnswerSkipped(feedback, reason);
      sendTechnicalIssueMailer(reason, feedback, false);
    } else if (meta.skippedUrl) {
      request.post(meta.skippedUrl, {
        question_id: data.questionId,
        skipped: true,
      });
      setSkipped(true);
      setCurrentChoice(null);
    }
    if (userResponses.length > 0) {
      setSelectedTab(TAB_RESPONSES);
    }
    setErrorMessage(null);
  };

  const skipOrSendFeedbackOption = () => {
    if (
      !sendFeedbackError &&
      (showSkipOption || showReportTechnicalDifficulties)
    ) {
      return (
        <>
          {technicalDifficultiesReported && (
            <div className="pt-24">
              <Text
                className="d-f ai-c c-success"
                variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
              >
                <Check size="2rem" className="mr-4" />
                {t(
                  'practice.on_demand_practice.audio_recording.skip_recording.success.success_short',
                )}
              </Text>
            </div>
          )}

          <SkippedOption
            skipRecording={skipRecording}
            disabled={skipDisabled}
            showTechnicalDifficulties
            i18n={i18n}
          />
        </>
      );
    }
    return null;
  };

  const renderSkipped = () => {
    if (skipped && !meta.finished) {
      return sendFeedbackError ? (
        <SkippedFailure
          {...props}
          mediaButtonClass={mediaButtonClass}
          recordButtonText={recordButtonText}
          uploadButtonText={uploadButtonText}
          finished={meta.finished}
          disabled={disabled}
          showTechnicalDifficulties
          reloadRecording={resetAnswer}
        />
      ) : (
        <div>
          <SkippedSuccess
            {...props}
            mediaButtonClass={mediaButtonClass}
            recordButtonText={recordButtonText}
            uploadButtonText={uploadButtonText}
            finished={meta.finished}
            disabled={disabled}
            showTechnicalDifficulties
            reloadRecording={resetAnswer}
          />
        </div>
      );
    }
    return null;
  };

  let contentComponent;
  switch (data.questionType) {
    case 'webcam_recording': {
      contentComponent = (
        <WebcamRecordingRepeatableQuestion
          disabled={disabled}
          handleBadUploadType={handleBadUploadType}
          recordButtonText={recordButtonText}
          saveMedia={saveMedia}
          setFinishedRecording={setFinishedRecording}
          setIsTabDisabled={setIsTabDisabled}
          setUploadFinished={setUploadFinished}
          setUploadStarted={setUploadStarted}
          seismicBiometricCompliance={seismicBiometricCompliance}
          enabledAutomatedAnalysis={enabledAutomatedAnalysis}
          setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
          completedAt={completedAt}
          {...props}
        />
      );
      break;
    }
    case 'screen_recording': {
      contentComponent = (
        <ScreenRecordingRepeatableQuestion
          disabled={disabled}
          handleBadUploadType={handleBadUploadType}
          hasZiggeoForAllMedia={hasZiggeoForAllMedia}
          provider={provider}
          recordButtonText={recordButtonText}
          saveMedia={saveMedia}
          setFinishedRecording={setFinishedRecording}
          setIsTabDisabled={setIsTabDisabled}
          setUploadFinished={setUploadFinished}
          setUploadStarted={setUploadStarted}
          seismicBiometricCompliance={seismicBiometricCompliance}
          {...props}
        />
      );
      break;
    }
    case 'audio_recording': {
      contentComponent = (
        <AudioRecordingRepeatableQuestion
          disabled={disabled}
          handleBadUploadType={handleBadUploadType}
          saveMedia={saveMedia}
          setFinishedRecording={setFinishedRecording}
          setIsTabDisabled={setIsTabDisabled}
          setUploadFinished={setUploadFinished}
          setUploadStarted={setUploadStarted}
          seismicBiometricCompliance={seismicBiometricCompliance}
          {...props}
        />
      );
      break;
    }

    default:
      contentComponent = null;
  }

  return (
    <div className={containerClasses}>
      {uploadTypeError && (
        <UploadFileTypeError
          reloadRecording={reloadRecording}
          questionType={questionType}
        />
      )}
      <div className="Response">
        {videoUploading && (
          <div className="bg-gray-lightest p-12 mb-32">
            <Icon name="warning_sign" className="mr-8" />
            <Text className="mr-4" variant={TEXT_VARIANTS.BODY_BOLD}>
              {t(
                'questions.question.video_response.new_navigation_warning_bold',
              )}
            </Text>
            <Text className="mr-4" variant={TEXT_VARIANTS.BODY}>
              {t('questions.question.video_response.new_navigation_warning')}
            </Text>
          </div>
        )}
        {renderSkipped()}
        {!errorMessage && !(skipped && !finished) && (
          <div className={recorderStyles}>
            <div className={`d-f fd-c ai-c ${warningStyles}`}>
              {userResponses.length === 9 && (
                <Text variant={TEXT_VARIANTS.BODY} className="d-f ai-e mb-32">
                  <AlertTriangle className="mr-4" />
                  {t('practice.repeatability.limit_warning')}
                </Text>
              )}
              {userResponses.length === 10 && (
                <Text
                  variant={TEXT_VARIANTS.INSTRUX_ERROR}
                  className="d-f ai-e mb-32"
                >
                  <AlertCircle color="#da3418" className="mr-4" />
                  {t('practice.repeatability.limit_reached')}
                </Text>
              )}
            </div>
            <div className={previewingStyles}>{contentComponent}</div>
          </div>
        )}
        {skipOrSendFeedbackOption()}
        {skipped && meta.finished && (
          <div className="d-f jc-sa ml-32 mr-32 pt-24">
            <Text
              className="d-f ai-c c-success"
              variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD}
            >
              <Check size="2rem" className="mr-4" />
              {t(
                'practice.on_demand_practice.audio_recording.skip_recording.success.success_message',
              )}
            </Text>
          </div>
        )}
        {givenAnswer && (
          <HighFiveShareableLink
            questionId={questionId}
            progressId={progressId}
            hasAnswerBeenSubmitted={!!givenAnswer}
            isGradable={isGradable}
          />
        )}
      </div>
    </div>
  );
};

export default RepeatableRecordingQuestion;
